import React from "react";
import { Link } from "react-router-dom";
import {
  BuildingOffice2Icon,
  MapPinIcon,
  UserIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { utcDateLabel } from "../../../utils/utcTime";
import { getRfiRequestFullAddressDisplay } from "../../../utils/myRfiGridRowMapper";

// Cards identify a specific vendor segment, so the street leads the address.
const composeLocation = (segment) =>
  getRfiRequestFullAddressDisplay(segment) || "Location not specified";

// Card grid shown above the read-only questionnaire in the Last Submitted Responses tab
// when the vendor has 2+ eligible past submissions. Default selection is the segment
// whose location matches the current pending request; if no match, none is pre-selected.
const SegmentSelectorGrid = ({ segments, selectedId, onSelect, returnToId }) => {
  if (!segments || segments.length < 2) return null;

  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        Select a previously submitted segment
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {segments.map((segment) => {
          const isActive = selectedId === segment.sponsor_rfi_response_id;
          const submittedOn = utcDateLabel(segment.release_date, "MMM D, YYYY");
          const submitterName = segment.submitter_name || "—";
          const approverName = segment.approver_name || segment.submitter_name || "—";
          const location = composeLocation(segment);

          return (
            <button
              key={segment.sponsor_rfi_response_id}
              type="button"
              onClick={() => onSelect(segment.sponsor_rfi_response_id)}
              className={`text-left rounded-lg border p-4 transition focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                isActive
                  ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200"
                  : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
              aria-pressed={isActive}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <BuildingOffice2Icon
                    className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <Link
                    to={
                      returnToId
                        ? `/RFI/${segment.questionnaire_id}/Details?returnTo=${returnToId}`
                        : `/RFI/${segment.questionnaire_id}/Details`
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-semibold text-indigo-700 hover:underline truncate"
                  >
                    {segment.sponsor_name || "Sponsor"}
                  </Link>
                </div>
                {isActive ? (
                  <CheckCircleIcon
                    className="h-5 w-5 text-indigo-600 shrink-0"
                    aria-hidden="true"
                  />
                ) : null}
              </div>

              <dl className="mt-3 space-y-1.5 text-xs text-gray-700">
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <span>{submittedOn}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <span className="truncate">{location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <span className="truncate">
                    Submitted by {submitterName}
                    {approverName !== submitterName ? ` · Approved by ${approverName}` : ""}
                  </span>
                </div>
              </dl>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentSelectorGrid;
